import React, { useState, useEffect } from 'react';
import { 
  Card, 
  List, 
  Tag, 
  Button, 
  Space, 
  Typography, 
  Avatar, 
  Badge,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  message
} from 'antd';
import { 
  BellOutlined, 
  CheckOutlined, 
  DeleteOutlined, 
  UserOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { getUserIdAsNumber } from '../utils/cognitoUtils';
import apiService, { Notification } from '../services/api';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const Notifications: React.FC = () => {
  const { user } = useCognitoAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await apiService.getNotifications(getUserIdAsNumber(user));
      setNotifications(data);
    } catch (error) {
      message.error('Erro ao carregar notificações.');
    }
    setLoading(false);
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await apiService.markNotificationAsRead(id);
      setNotifications(notifications.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      ));
      message.success('Notificação marcada como lida.');
    } catch (error) {
      message.error('Erro ao marcar notificação como lida.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiService.deleteNotification(id);
      setNotifications(notifications.filter(notification => notification.id !== id));
      message.success('Notificação excluída.');
    } catch (error) {
      message.error('Erro ao excluir notificação.');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    try {
      await apiService.markAllNotificationsAsRead(getUserIdAsNumber(user));
      setNotifications(notifications.map(notification => ({ ...notification, read: true })));
      message.success('Todas as notificações marcadas como lidas.');
    } catch (error) {
      message.error('Erro ao marcar todas as notificações como lidas.');
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'assignment': return 'blue';
      case 'duplicate_lead': return 'orange';
      case 'info': return 'green';
      case 'warning': return 'gold';
      case 'error': return 'red';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'assignment': return <UserOutlined />;
      case 'duplicate_lead': return <ExclamationCircleOutlined />;
      case 'info': return <InfoCircleOutlined />;
      case 'warning': return <ExclamationCircleOutlined />;
      case 'error': return <ExclamationCircleOutlined />;
      default: return <BellOutlined />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'assignment': return 'Atribuição';
      case 'duplicate_lead': return 'Lead Duplicado';
      case 'info': return 'Informação';
      case 'warning': return 'Aviso';
      case 'error': return 'Erro';
      default: return type;
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    const typeMatch = filterType === 'all' || notification.notification_type === filterType;
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'unread' && !notification.read) || 
      (filterStatus === 'read' && notification.read);
    const searchMatch = !searchText || 
      notification.title.toLowerCase().includes(searchText.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchText.toLowerCase());
    return typeMatch && statusMatch && searchMatch;
  });

  const unreadCount = notifications.filter(n => !n.read).length;
  const expiredCount = notifications.filter(n => new Date(n.expires_at) < new Date()).length;

  if (!user) {
    return null;
  }

  return (
    <div>
      <Title level={2}>Notificações</Title>
      
      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total"
              value={notifications.length}
              prefix={<BellOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Não Lidas"
              value={unreadCount}
              valueStyle={{ color: '#1890ff' }}
              prefix={<Badge count={unreadCount} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Expiradas"
              value={expiredCount}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Ativas"
              value={notifications.length - expiredCount}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Input.Search
              placeholder="Procurar notificações..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Tipo"
              value={filterType}
              onChange={setFilterType}
              style={{ width: '100%' }}
            >
              <Option value="all">Todos os Tipos</Option>
              <Option value="assignment">Atribuições</Option>
              <Option value="duplicate_lead">Leads Duplicados</Option>
              <Option value="info">Informações</Option>
              <Option value="warning">Avisos</Option>
              <Option value="error">Erros</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Estado"
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: '100%' }}
            >
              <Option value="all">Todos os Estados</Option>
              <Option value="unread">Não Lidas</Option>
              <Option value="read">Lidas</Option>
            </Select>
          </Col>
          <Col span={10}>
            <Space>
              <Button 
                type="primary" 
                onClick={handleMarkAllAsRead}
                disabled={unreadCount === 0}
              >
                Marcar Todas como Lidas
              </Button>
              <Button 
                danger 
                onClick={() => setNotifications([])}
              >
                Limpar Todas
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Notifications List */}
      <Card>
        <List
          itemLayout="horizontal"
          dataSource={filteredNotifications}
          loading={loading}
          renderItem={(notification) => (
            <List.Item
              actions={[
                !notification.read && (
                  <Button 
                    type="text" 
                    icon={<CheckOutlined />}
                    onClick={() => handleMarkAsRead(notification.id)}
                  >
                    Marcar como Lida
                  </Button>
                ),
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(notification.id)}
                >
                  Excluir
                </Button>
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={
                  <Badge dot={!notification.read}>
                    <Avatar 
                      icon={getTypeIcon(notification.notification_type)}
                      style={{ backgroundColor: getTypeColor(notification.notification_type) }}
                    />
                  </Badge>
                }
                title={
                  <Space>
                    <span style={{ 
                      fontWeight: !notification.read ? 'bold' : 'normal' 
                    }}>
                      {notification.title}
                    </span>
                    <Tag color={getTypeColor(notification.notification_type)}>
                      {getTypeLabel(notification.notification_type)}
                    </Tag>
                  </Space>
                }
                description={
                  <div>
                    <div>{notification.message}</div>
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">
                        Criada: {dayjs(notification.created_at).tz('Europe/Lisbon').format('DD/MM/YYYY HH:mm')} | 
                        Expira: {dayjs(notification.expires_at).tz('Europe/Lisbon').format('DD/MM/YYYY HH:mm')}
                      </Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} notificações`,
          }}
        />
      </Card>
    </div>
  );
};

export default Notifications; 